import { EmptyState } from "@/components/feedback/empty-state";
import { pathFromSeries } from "@/lib/chart/path-from-series";

export interface DailyExpensePoint {
  date: string;
  count: number;
  amount: number;
}

export interface DailyExpensesChartProps {
  data: DailyExpensePoint[];
}

const WIDTH = 640;
const HEIGHT = 200;

export function DailyExpensesChart({ data }: DailyExpensesChartProps) {
  if (data.length === 0) {
    return <EmptyState title="No expenses in the last 30 days" />;
  }

  const points = data.map((d, i) => ({ x: i, y: d.count }));
  const { line, area } = pathFromSeries(points, WIDTH, HEIGHT);

  const peak = data.reduce((max, d) => (d.count > max.count ? d : max), data[0]);
  const first = data[0];
  const last = data[data.length - 1];

  return (
    <div className="flex flex-col gap-2">
      <div
        data-testid="chart-peak"
        className="text-xs text-fc-text-dim"
      >
        Peak {peak.date} · <span className="font-mono text-fc-text">{peak.count}</span>
      </div>

      <svg
        data-testid="daily-chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT + 20}`}
        width="100%"
        height={HEIGHT + 20}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="dc-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#34D399" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
          </linearGradient>
        </defs>

        <g stroke="rgba(255,255,255,0.05)" strokeWidth="1">
          <line x1="0" y1={HEIGHT * 0.25} x2={WIDTH} y2={HEIGHT * 0.25} />
          <line x1="0" y1={HEIGHT * 0.5} x2={WIDTH} y2={HEIGHT * 0.5} />
          <line x1="0" y1={HEIGHT * 0.75} x2={WIDTH} y2={HEIGHT * 0.75} />
        </g>

        <path d={area} fill="url(#dc-grad)" />
        <path d={line} fill="none" stroke="#34D399" strokeWidth="1.8" />

        <g fill="#6C7480" fontFamily="JetBrains Mono, monospace" fontSize="10">
          <text x="0" y={HEIGHT + 14}>{first.date}</text>
          <text x={WIDTH - 60} y={HEIGHT + 14}>{last.date}</text>
        </g>
      </svg>
    </div>
  );
}
